import { useState, useEffect } from "react";
import { gitlabApiPrefix } from "../components/SupabaseClient";
import { fetchAllGitLabPages } from "../utils/gitlabPagination";

/**
 * Fetches the list of projects belong to a GitLab subgroup.
 *
 * @param {string} subgroupPath  Full subgroup path, e.g. "efabric/base-images"
 * @returns {{ projects: object[], loading: boolean, error: string|null }}
 */
export const useGitLabProjects = (subgroupPath) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!subgroupPath) return;

    // Guard prevents a stale/slow response from overwriting newer state or
    // calling setState after unmount.
    const controller = new AbortController();
    const { signal } = controller;

    const fetchProjects = async () => {
      setLoading(true);
      setError(null);

      try {
        const encoded = encodeURIComponent(subgroupPath);
        const data = await fetchAllGitLabPages(
          `${gitlabApiPrefix}/api/v4/groups/${encoded}/projects?per_page=100&order_by=name&sort=asc`,
          { signal },
        );
        if (signal.aborted) return;
        setProjects(data);
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("[useGitLabProjects] Failed to fetch projects:", err);
        setError(err.message);
        setProjects([]);
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    };

    fetchProjects();

    return () => controller.abort();
  }, [subgroupPath]);

  return { projects, loading, error };
};
